import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { 
  CheckCircle, 
  XCircle, 
  PhoneCall, 
  RefreshCw,
  MonitorUp, 
  ArrowLeft,
  Layers,
  MapPin,
  Loader2,
  Users,
  Clock,
  User
} from "lucide-react";
import ticketAPI from "../axios/ticketAPI";
import serviceApi from "../axios/serviceApi";
import lineAPI from "../axios/lineAPI";
import { toast } from "react-toastify";

// --- HELPER: Format Timer ---
const formatDuration = (seconds) => {
  if (!seconds) return "00:00:00";
  const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

// --- SUB-COMPONENT: ADVANCED PiP Window ---
const PipQueueWindow = ({ 
    ticket, 
    stats, 
    waitingList, 
    onNext, 
    onFinish, 
    onCancel,
    services,
    lines,
    currentServiceId,
    currentLineId,
    onServiceChange,
    onLineChange,
    isLoading
}) => {
  return (
    <div className="h-full flex flex-col bg-slate-950 text-white p-3 font-sans box-border select-none overflow-hidden relative">
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-slate-900/90 z-50 flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-indigo-500 mb-2" size={32} />
            <span className="text-xs font-medium text-slate-300">Loading data...</span>
        </div>
      )}

      {/* --- HEADER CONTROLS --- */}
      <div className="flex flex-col gap-2 border-b border-slate-800 pb-2 mb-2 shrink-0">
        <div className="flex items-center gap-2 bg-slate-800/50 rounded px-2 py-1 border border-slate-700/50">
            <Layers size={14} className="text-indigo-400 shrink-0"/>
            <select 
                value={currentServiceId || ""} 
                onChange={(e) => onServiceChange(e.target.value)}
                className="bg-transparent border-none text-xs text-slate-200 w-full focus:ring-0 outline-none cursor-pointer appearance-none py-0.5"
                disabled={isLoading}
            >
                {services.map(s => <option key={s.id} value={s.id} className="text-black">{s.name}</option>)}
            </select>
        </div>

        <div className="flex items-center gap-2 bg-slate-800/50 rounded px-2 py-1 border border-slate-700/50">
            <MapPin size={14} className="text-emerald-400 shrink-0"/>
            <select 
                value={currentLineId || ""}
                onChange={(e) => onLineChange(e.target.value)} 
                className="bg-transparent border-none text-xs text-slate-200 w-full focus:ring-0 outline-none cursor-pointer appearance-none py-0.5"
                disabled={isLoading}
            >
                {lines.map(l => <option key={l.id} value={l.id} className="text-black">{l.name}</option>)}
            </select>
        </div>
      </div>

      {/* --- STATS BAR --- */}
      <div className="flex justify-between items-center mb-2 px-1 shrink-0">
        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Queue Status</div>
        <div className="flex gap-3 text-xs font-mono">
            <span className="text-amber-400 font-bold">Wait: {stats.waiting}</span>
            <span className="text-slate-400">Total: {stats.total}</span>
        </div>
      </div>

      {/* --- MAIN DISPLAY (CURRENT TICKET) --- */}
      <div className="flex flex-col items-center justify-center text-center bg-gradient-to-b from-slate-800 to-slate-900 rounded-lg mb-2 py-3 border border-slate-700/50 shrink-0 min-h-[100px] shadow-inner">
        {ticket ? (
          <div className="animate-fadeIn w-full px-2">
             <div className="text-[10px] text-emerald-400 uppercase tracking-widest font-bold mb-0.5">Now Serving</div>
             <h1 className="text-4xl font-black text-white mb-1 tracking-tighter truncate">{ticket.queueNumber}</h1>
             <p className="text-xs text-indigo-200 font-medium truncate">{ticket.name}</p>
          </div>
        ) : (
          <div className="text-slate-600 flex flex-col items-center opacity-50">
            <span className="text-lg font-bold">READY</span>
            <span className="text-[10px]">No active ticket</span>
          </div>
        )}
      </div>

      {/* --- WAITING LIST (SCROLLABLE) --- */}
      <div className="flex-1 overflow-y-auto mb-2 pr-1 custom-scrollbar min-h-0">
        <div className="text-[10px] text-slate-500 font-bold uppercase mb-1 flex items-center gap-1 sticky top-0 bg-slate-950 py-1 z-10">
            <Users size={10}/> Next in line ({waitingList.length})
        </div>
        {waitingList.length === 0 ? (
            <div className="text-center text-[10px] text-slate-600 py-4 italic border border-dashed border-slate-800 rounded">Queue is empty</div>
        ) : (
            <div className="space-y-1">
                {waitingList.map((t, idx) => (
                    <div key={t.id} className="flex justify-between items-center bg-slate-800/80 px-2 py-1.5 rounded border border-slate-700/50 text-xs">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <span className="text-indigo-400 font-mono font-bold w-4 text-center">{idx + 1}.</span>
                            <span className="font-bold text-white truncate">{t.queueNumber}</span>
                        </div>
                        <span className="text-slate-400 truncate max-w-[80px]">{t.name}</span>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* --- ACTIONS --- */}
      <div className="grid grid-cols-2 gap-2 shrink-0 pt-1 border-t border-slate-800">
        <button 
            onClick={onNext} 
            disabled={isLoading}
            className="col-span-2 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded font-bold text-sm shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PhoneCall size={16} className={isLoading ? "" : "animate-pulse"}/> CALL NEXT
        </button>
        <button 
            onClick={onFinish} 
            disabled={!ticket || isLoading} 
            className="py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-600 rounded font-bold text-xs transition-colors"
        >
            Finish
        </button>
        <button 
            // --- SỬA LỖI TẠI ĐÂY: Truyền true để bỏ qua xác nhận (Skip Confirm) ---
            onClick={() => onCancel(true)} 
            disabled={!ticket || isLoading} 
            className="py-1.5 bg-rose-600 hover:bg-rose-500 disabled:bg-slate-800 disabled:text-slate-600 rounded font-bold text-xs transition-colors"
        >
            Cancel
        </button>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
const CounterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const lineId = searchParams.get("lineId");
  const returnServiceId = location.state?.returnServiceId;

  // Context & Data State
  const [services, setServices] = useState([]);
  const [lines, setLines] = useState([]); 
  const [currentServiceId, setCurrentServiceId] = useState(null);
  
  const [lineInfo, setLineInfo] = useState({ id: lineId, name: `Loading...` });
  const [tickets, setTickets] = useState([]);
  const [currentTicket, setCurrentTicket] = useState(null);
  
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false); 
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pipWindow, setPipWindow] = useState(null);

  // --- 1. INITIALIZE ---
  useEffect(() => {
      const initContext = async () => {
          try {
              const res = await serviceApi.getService();
              let svcList = [];
              if (res.data && Array.isArray(res.data.data)) svcList = res.data.data;
              else if (res.data?.data?.services) svcList = res.data.data.services;
              else if (Array.isArray(res.data)) svcList = res.data;
              setServices(svcList);

              if (!currentServiceId) {
                  if (returnServiceId) setCurrentServiceId(returnServiceId);
                  else if (svcList.length > 0) setCurrentServiceId(svcList[0].id);
              }
          } catch (e) { console.error(e); }
      };
      initContext();
  }, []);

  // Fetch Lines when Service Changes
  useEffect(() => {
      const fetchLines = async () => {
          if (!currentServiceId) return;
          try {
              const res = await lineAPI.getLine(currentServiceId);
              let rawLines = [];
              if (res.data?.data) {
                  rawLines = Array.isArray(res.data.data) ? res.data.data : res.data.data.lines || [];
              }
              setLines(rawLines);
          } catch (e) { console.error(e); }
      };
      fetchLines();
  }, [currentServiceId]);

  // --- 2. FETCH TICKETS ---
  const fetchTickets = useCallback(async (isBackground = false) => {
    if (!lineId) return;
    if (!isBackground) setIsLoading(true);

    try {
      const res = await ticketAPI.getAll(lineId, { page: 1, limit: 100 });
      let rawData = [];
      const responseBody = res.data ? res.data : res;
      
      if (responseBody.data && Array.isArray(responseBody.data)) rawData = responseBody.data;
      else if (responseBody.data?.tickets && Array.isArray(responseBody.data.tickets)) rawData = responseBody.data.tickets;
      else if (Array.isArray(responseBody)) rawData = responseBody;
      else if (responseBody.tickets && Array.isArray(responseBody.tickets)) rawData = responseBody.tickets;

      const formattedTickets = rawData.map(t => ({
          ...t,
          queueNumber: t.queueNumber || t.queue_number || `#${t.id}`,
          name: t.name || t.user?.name || "Guest",
          phone: t.phone || t.user?.phone || "N/A",
          status: (t.status || "waiting").toLowerCase(),
          joined_at: t.joined_at || t.created_at || new Date().toISOString()
      }));

      setTickets(formattedTickets);

      const activeTicket = formattedTickets.find(t => 
        ['serving', 'processing', 'calling', 'active'].includes(t.status)
      );
      setCurrentTicket(activeTicket || null);
      
      if (formattedTickets.length > 0 && formattedTickets[0].line_name) {
          setLineInfo(prev => ({ ...prev, name: formattedTickets[0].line_name }));
      }

    } catch (err) {
      if (!isBackground && err.response?.status !== 404) toast.error("Connection error");
    } finally {
      if (!isBackground) setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [lineId]);

  useEffect(() => {
      fetchTickets(false);
      const interval = setInterval(() => fetchTickets(true), 3000);
      return () => clearInterval(interval);
  }, [fetchTickets]);

  useEffect(() => {
      if (lines.length > 0) {
          const currentLine = lines.find(l => l.id === parseInt(lineId));
          if (currentLine) setLineInfo(prev => ({ ...prev, name: currentLine.name }));
      }
  }, [lines, lineId]);

  // --- 3. SWITCHING LOGIC ---
  const handleSwitchService = (newServiceId) => {
      setIsLoading(true);
      setCurrentServiceId(parseInt(newServiceId));
  };

  const handleSwitchLine = (newLineId) => {
      setIsLoading(true);
      setTickets([]); 
      setCurrentTicket(null);
      navigate(`/counter?lineId=${newLineId}`, { 
          state: { returnServiceId: currentServiceId } 
      });
  };

  // Auto-select first line logic
  useEffect(() => {
      if (lines.length > 0 && currentServiceId) {
          const currentLineExists = lines.find(l => l.id === parseInt(lineId));
          if (!currentLineExists) {
              handleSwitchLine(lines[0].id);
          }
      }
  }, [lines]); 

  // --- TIMER ---
  useEffect(() => {
    if (currentTicket) {
      clearInterval(timerRef.current);
      if (currentTicket.served_at) {
          const startTime = new Date(currentTicket.served_at).getTime();
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setTimer(elapsed > 0 ? elapsed : 0);
      } else { setTimer(0); }
      timerRef.current = setInterval(() => setTimer(p => p + 1), 1000);
    } else {
      setTimer(0);
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [currentTicket?.id]);

  // --- ACTIONS ---
  const handleCallNext = async () => {
    if (!lineId) return;
    try {
      await ticketAPI.putTicket(lineId);
      toast.success("Calling Next...");
      fetchTickets(false); 
    } catch (err) { toast.error("Error calling next"); }
  };

  const handleFinish = async () => {
    if (!currentTicket) return;
    try {
      await ticketAPI.putFinish(currentTicket.id);
      toast.success("Finished!");
      fetchTickets(false);
    } catch (err) { toast.error("Error finishing"); }
  };

  const handleCancel = async (skipConfirm = false) => {
    if (!currentTicket) return;
    
    // Nếu skipConfirm = false (tức là không bỏ qua xác nhận), thì hiện popup
    if (!skipConfirm && !window.confirm("Cancel this ticket?")) return;
    
    try {
      await ticketAPI.putCancel(currentTicket.id);
      toast.success("Cancelled!");
      fetchTickets(false);
    } catch (err) { toast.error("Error cancelling"); }
  };

  const togglePiP = async () => {
    if (!("documentPictureInPicture" in window)) return toast.error("Browser not supported");
    if (pipWindow) { pipWindow.close(); setPipWindow(null); return; }
    try {
      const pip = await window.documentPictureInPicture.requestWindow({ width: 360, height: 650 }); 
      [...document.styleSheets].forEach((s) => {
        try { pip.document.head.appendChild(s.ownerNode.cloneNode(true)); } catch (e) {}
      });
      pip.addEventListener("pagehide", () => setPipWindow(null));
      setPipWindow(pip);
    } catch (err) { console.error(err); }
  };

  const handleBackToMonitor = () => {
      navigate('/monitor', { state: { returnServiceId: currentServiceId } });
  };

  if (!lineId) return null;

  // --- CALCULATION ---
  const waitingTickets = tickets.filter(t => ['waiting', 'pending'].includes(t.status));
  const activeCount = currentTicket ? 1 : 0;
  const totalActiveInQueue = waitingTickets.length + activeCount;

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-sans text-slate-800 overflow-hidden">
      
      {/* PiP Portal */}
      {pipWindow && createPortal(
        <PipQueueWindow 
          ticket={currentTicket}
          stats={{ 
              waiting: waitingTickets.length, 
              total: totalActiveInQueue 
          }}
          waitingList={waitingTickets} 
          onNext={handleCallNext}
          onFinish={handleFinish}
          onCancel={handleCancel} // Truyền hàm cancel
          
          services={services}
          lines={lines}
          currentServiceId={currentServiceId}
          currentLineId={lineId}
          onServiceChange={handleSwitchService}
          onLineChange={handleSwitchLine}
          isLoading={isLoading}
        />,
        pipWindow.document.body
      )}

      {/* HEADER */}
      <header className="bg-white text-slate-800 h-16 flex items-center justify-between px-6 shadow-sm border-b border-slate-200 shrink-0 z-20">
        <div className="flex items-center gap-4">
          <button 
            onClick={handleBackToMonitor}
            className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-bold text-sm uppercase tracking-wide hidden sm:block">Back to Monitor</span>
          </button>
          
          <div className="h-8 w-px bg-slate-200 mx-2 hidden sm:block"></div>

          <div className="flex flex-col">
             <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
                {isLoading && <Loader2 size={18} className="animate-spin text-indigo-500"/>}
                {lineInfo.name || `Line #${lineId}`}
             </h1>
             <span className="text-xs text-slate-500 font-medium">
                {services.find(s => s.id === parseInt(currentServiceId))?.name || "Service"}
             </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-4 mr-4 text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-full border border-slate-200">
                <span className="flex items-center gap-1.5"><Users size={14}/> Waiting: <strong className="text-slate-800">{waitingTickets.length}</strong></span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <span className="flex items-center gap-1.5"><Layers size={14}/> Total: <strong className="text-slate-800">{totalActiveInQueue}</strong></span>
            </div>

            <button 
              onClick={() => { setIsRefreshing(true); fetchTickets(false); }} 
              disabled={isRefreshing}
              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title="Refresh Data"
            >
              <RefreshCw size={20} className={isRefreshing ? "animate-spin" : ""}/> 
            </button>
            <button 
              onClick={togglePiP} 
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm border transition-all shadow-sm ${
                  pipWindow 
                  ? 'bg-indigo-50 text-indigo-600 border-indigo-200 ring-2 ring-indigo-100' 
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <MonitorUp size={16}/> {pipWindow ? "PiP Active" : "Open Mini Mode"}
            </button>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="flex-1 p-6 grid grid-cols-12 gap-6 overflow-hidden relative">
        
        {/* Loading Overlay */}
        {isLoading && !pipWindow && (
            <div className="absolute inset-0 bg-white/80 z-50 flex flex-col items-center justify-center backdrop-blur-sm rounded-xl">
                <Loader2 size={48} className="text-indigo-600 animate-spin mb-4" />
                <span className="text-slate-500 font-medium animate-pulse">Updating counter data...</span>
            </div>
        )}

        {/* LEFT COLUMN: CURRENT SERVING + ACTIONS */}
        <div className="col-span-12 lg:col-span-8 flex flex-col gap-6 h-full overflow-y-auto pr-2 custom-scrollbar">
            
            {/* BIG CARD: CURRENT SERVING */}
            <section className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col relative overflow-hidden group min-h-[300px]">
               {/* Background decoration */}
               <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full -mr-20 -mt-20 opacity-50 group-hover:scale-110 transition-transform duration-700"></div>
               
               <div className="flex-1 flex flex-col items-center justify-center text-center p-10 relative z-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold uppercase tracking-wider mb-6">
                      <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse"></span>
                      Now Serving
                  </div>
                  
                  {currentTicket ? (
                      <div className="animate-fade-in-up">
                          <div className="text-9xl xl:text-[10rem] font-black text-slate-900 tracking-tighter leading-none mb-6">
                             {currentTicket.queueNumber}
                          </div>
                          
                          <div className="flex flex-col gap-1 mb-8">
                              <div className="text-3xl font-bold text-slate-800 truncate px-4">
                                 {currentTicket.name}
                              </div>
                              <div className="text-sm text-slate-400 font-medium flex items-center justify-center gap-2">
                                 <PhoneCall size={14}/> {currentTicket.phone}
                              </div>
                          </div>

                          <div className="inline-flex items-center gap-3 bg-slate-100 px-6 py-3 rounded-xl">
                              <div className="text-sm text-slate-500 font-medium">Serving Time</div>
                              <div className="text-2xl font-mono font-bold text-indigo-600 min-w-[100px]">
                                 {formatDuration(timer)}
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="flex flex-col items-center justify-center py-10 opacity-40">
                          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                              <Layers size={48} className="text-slate-400" />
                          </div>
                          <div className="text-2xl font-bold text-slate-400">Counter Ready</div>
                          <p className="text-slate-400 mt-2">Waiting for next customer...</p>
                      </div>
                  )}
               </div>
            </section>

            {/* ACTION BUTTONS (BIG & BOLD) */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
               <button 
                 onClick={handleCallNext}
                 disabled={!lineId || isLoading}
                 className="h-24 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white rounded-2xl shadow-lg shadow-indigo-200 transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group overflow-hidden relative border border-indigo-500"
               >
                  <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  <PhoneCall size={32} className="relative z-10 group-hover:scale-110 transition-transform" />
                  <span className="text-lg font-bold uppercase tracking-wider relative z-10">Call Next</span>
               </button>

               <button 
                 onClick={handleFinish}
                 disabled={!currentTicket || isLoading}
                 className="h-24 bg-white border-2 border-emerald-100 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 active:scale-[0.98] rounded-2xl transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:bg-slate-50 disabled:border-slate-100 disabled:text-slate-400"
               >
                  <CheckCircle size={32} />
                  <span className="text-lg font-bold uppercase tracking-wider">Finish</span>
               </button>

               <button 
                 onClick={() => handleCancel(false)}
                 disabled={!currentTicket || isLoading}
                 className="h-24 bg-white border-2 border-rose-100 text-rose-600 hover:bg-rose-50 hover:border-rose-200 active:scale-[0.98] rounded-2xl transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:bg-slate-50 disabled:border-slate-100 disabled:text-slate-400"
               >
                  <XCircle size={32} />
                  <span className="text-lg font-bold uppercase tracking-wider">Cancel</span>
               </button>
            </section>
        </div>

        {/* RIGHT COLUMN: WAITING LIST */}
        <div className="col-span-12 lg:col-span-4 h-full flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
           <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Users className="text-indigo-500" size={20}/> 
                  Waiting Queue
              </h3>
              <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {waitingTickets.length}
              </span>
           </div>

           <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {waitingTickets.length === 0 ? (
                 <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-3 opacity-60">
                     <Clock size={40} strokeWidth={1.5} />
                     <span className="text-sm font-medium">No visitors waiting</span>
                 </div>
              ) : (
                 waitingTickets.map((t, idx) => (
                    <div key={t.id} className="group flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:border-indigo-200 hover:shadow-md transition-all duration-200 cursor-default">
                       <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600 flex items-center justify-center font-bold text-sm transition-colors shrink-0">
                          {idx + 1}
                       </div>
                       <div className="flex-1 min-w-0">
                          <div className="text-lg font-bold text-slate-800 leading-tight mb-1">
                             {t.queueNumber}
                          </div>
                          <div className="text-sm text-slate-500 font-medium truncate flex items-center gap-1.5">
                             <User size={14}/> {t.name}
                          </div>
                       </div>
                       <div className="text-xs text-slate-400 font-mono bg-slate-50 px-2 py-1 rounded">
                          {new Date(t.joined_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                       </div>
                    </div>
                 ))
              )}
           </div>
        </div>

      </main>
    </div>
  );
};

export default CounterPage;